import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, concat, defer} from 'rxjs';
import {map} from 'rxjs/operators';
import {ApiClientService} from '../api-client/api-client.service';
import {FileDownloadService} from '../file-download/file-download.service';

export interface CsvExportOptions {
  /** API route after `/api`, e.g. `/conversion/download`. */
  endpoint: string;
  /** Filter parameters; must include `startDate` and `endDate` as ISO 8601 strings. */
  params: Record<string, any>;
  /** Final filename (with or without `.csv`). */
  filename: string;
  /** Number of days per chunk. Default: 7. */
  chunkDays?: number;
}

export type CsvExportEvent =
  | { type: 'progress'; current: number; total: number }
  | { type: 'complete'; chunkCount: number; truncatedChunks: number };

interface ChunkRange {
  startDate: Date;
  endDate: Date;
}

const DEFAULT_CHUNK_DAYS = 7;
const TRUNCATION_MARKER = 'WARNING: Export interrupted';

@Injectable({
  providedIn: 'root'
})
export class CsvExportService {
  constructor(
    private apiClient: ApiClientService,
    private http: HttpClient,
    private fileDownload: FileDownloadService
  ) {
  }

  export(options: CsvExportOptions): Observable<CsvExportEvent> {
    const startDate = this.parseDate(options.params['startDate']);
    const endDate = this.parseDate(options.params['endDate']);

    if (!startDate || !endDate) {
      throw new Error('CsvExportService: startDate and endDate are required in params.');
    }
    if (startDate.getTime() > endDate.getTime()) {
      throw new Error('CsvExportService: startDate must be before or equal to endDate.');
    }

    const chunkDays = options.chunkDays ?? DEFAULT_CHUNK_DAYS;
    if (chunkDays <= 0) {
      throw new Error('CsvExportService: chunkDays must be greater than 0.');
    }

    const ranges = this.buildChunks(startDate, endDate, chunkDays);
    const total = ranges.length;

    return new Observable<CsvExportEvent>(subscriber => {
      const parts: BlobPart[] = [];
      let bomEmitted = false;
      let truncatedChunks = 0;
      let current = 0;

      // Initial progress so the UI can render "0 / N" before the first
      // network round-trip completes.
      subscriber.next({ type: 'progress', current: 0, total });

      const subscription = concat(
        ...ranges.map((range, index) =>
          defer(() => this.fetchChunk(options, range, index === 0))
        )
      ).subscribe({
        next: (chunk) => {
          const text = chunk.text;

          if (text.length > 0 && !bomEmitted) {
            // HttpClient (responseType: 'text') silently strips the UTF-8 BOM
            // returned by the server, so Excel would no longer detect UTF-8.
            // Re-emit the BOM as the first byte sequence of the final Blob,
            // unless the runtime happened to preserve it on this chunk.
            if (text.charCodeAt(0) !== 0xFEFF) {
              parts.push('\uFEFF');
            }
            bomEmitted = true;
          }

          if (text.length > 0) {
            parts.push(text);
          }
          if (chunk.truncated) {
            truncatedChunks++;
          }

          current++;
          subscriber.next({ type: 'progress', current, total });
        },
        complete: () => {
          this.triggerDownload(parts, this.normalizeFilename(options.filename), options.params);
          subscriber.next({ type: 'complete', chunkCount: total, truncatedChunks });
          subscriber.complete();
        },
        error: (err) => subscriber.error(err)
      });

      return () => subscription.unsubscribe();
    });
  }

  private fetchChunk(options: CsvExportOptions, range: ChunkRange, withHeaders: boolean): Observable<{ text: string; truncated: boolean }> {
    const chunkParams = {
      ...options.params,
      startDate: range.startDate.toISOString(),
      endDate: range.endDate.toISOString(),
      withHeaders: withHeaders ? 'true' : 'false'
    };

    const url = this.apiClient.getUrl(options.endpoint, chunkParams);

    return this.http
      .get(url, { responseType: 'text' })
      .pipe(map(text => this.stripWarningLine(text)));
  }

  private stripWarningLine(text: string): { text: string; truncated: boolean } {
    if (!text) {
      return { text: '', truncated: false };
    }

    // The server appends the WARNING line as the very last row when the
    // 25 s timer fires, so we always inspect the last non-empty line.
    const hadTrailingNewline = text.endsWith('\n');
    const body = hadTrailingNewline ? text.slice(0, -1) : text;

    const newlineIdx = body.lastIndexOf('\n');
    const lastLine = newlineIdx === -1 ? body : body.slice(newlineIdx + 1);

    if (!lastLine.startsWith(TRUNCATION_MARKER)) {
      return { text, truncated: false };
    }

    if (newlineIdx === -1) {
      return { text: '', truncated: true };
    }

    // Keep the trailing newline so concatenation with the next chunk stays clean.
    return { text: body.slice(0, newlineIdx + 1), truncated: true };
  }

  private buildChunks(startDate: Date, endDate: Date, chunkDays: number): ChunkRange[] {
    const ranges: ChunkRange[] = [];
    const chunkMs = chunkDays * 24 * 60 * 60 * 1000;

    let cursor = startDate.getTime();
    const end = endDate.getTime();

    while (cursor <= end) {
      let chunkEnd = cursor + chunkMs - 1;
      if (chunkEnd > end) {
        chunkEnd = end;
      }
      ranges.push({
        startDate: new Date(cursor),
        endDate: new Date(chunkEnd)
      });
      // 1 ms gap to guarantee no boundary duplicate, regardless of whether
      // the server uses inclusive or exclusive comparisons.
      cursor = chunkEnd + 1;
    }

    return ranges;
  }

  private triggerDownload(parts: BlobPart[], filename: string, params: Record<string, any>): void {
    this.fileDownload.saveBlob(new Blob(parts, { type: 'text/csv;charset=utf-8' }), filename, params);
  }

  private parseDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  private normalizeFilename(filename: string): string {
    return filename.toLowerCase().endsWith('.csv') ? filename : `${filename}.csv`;
  }
}
